// Ported from legacy ui/index.html formatting utilities.

// Defaults mirrored from bot config; fetched at runtime by consumers if needed.
const MAKER_FEE_RATE = 0.0002;
const TAKER_FEE_RATE = 0.0007;

// Magnitude-based decimal fallback when no symbol precision is known.
function magnitudeDecimals(n: number): number {
  const abs = Math.abs(n);
  if (!isFinite(abs)) return 2;
  if (abs >= 100) return 2;
  if (abs >= 10) return 3;
  if (abs >= 1) return 4;
  return 6;
}

/**
 * fmt(value)                   — magnitude-based decimals
 * fmt(value, 4)                — fixed 4 decimals
 * fmt(value, 'BTC/USDT:USDT') — lookup hardcoded tick precision, fallback to magnitude
 */
export function fmt(n: number | null | undefined, decimalsOrSymbol?: number | string): string {
  const num = Number(n ?? 0);
  let decimals: number | undefined;

  if (typeof decimalsOrSymbol === "number") {
    decimals = decimalsOrSymbol;
  } else if (typeof decimalsOrSymbol === "string") {
    // Hardcoded defaults; extend as more instruments are added.
    const PRECISION: Record<string, number> = {
      "BTC/USDT:USDT": 1,
      "ETH/USDT:USDT": 2,
      "SOL/USDT:USDT": 3,
    };
    decimals = PRECISION[decimalsOrSymbol];
  }

  if (decimals === undefined) {
    decimals = magnitudeDecimals(num);
  }

  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** ±X.XX% */
export function fmtPct(v: number): string {
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}

/** Duration in minutes → "1H" / "30M" */
export function fmtDur(m: number): string {
  return m >= 60 ? `${(m / 60).toFixed(0)}H` : `${m}M`;
}

/** Tailwind class for a numeric PnL value. */
export function pnlClass(n: number): "text-success" | "text-danger" | "text-muted" {
  if (n > 0) return "text-success";
  if (n < 0) return "text-danger";
  return "text-muted";
}

/** StatCard tone for a numeric PnL value. */
export function pnlTone(n: number): "success" | "danger" | "neutral" {
  if (n > 0) return "success";
  if (n < 0) return "danger";
  return "neutral";
}

/**
 * timeAgo — compact relative timestamp.
 * Accepts an ISO string or any Date-parseable value.
 */
export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/**
 * Classify a realized fee as maker or taker by comparing implied rate
 * (fee / notional) against the midpoint of maker + taker rates.
 */
export function classifyFee(
  fee: number,
  price: number,
  qty: number
): { label: "maker" | "taker" | ""; title: string } {
  if (!fee || !price || !qty) return { label: "", title: "" };
  const rate = fee / (price * qty);
  const threshold = (MAKER_FEE_RATE + TAKER_FEE_RATE) / 2;
  const isMaker = rate < threshold;
  const pct = (rate * 100).toFixed(4);
  return {
    label: isMaker ? "maker" : "taker",
    title: `${isMaker ? "Maker" : "Taker"} fee — rate ${pct}% of notional`,
  };
}
